import { useCallback, useEffect, useRef, useState } from 'react'
import { isAxiosError } from 'axios'
import {
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Select,
  Text,
  Tooltip,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react'
import { ArrowRightLeft, ChevronDown, ChevronUp, Headphones, Phone, PhoneCall, PhoneOff, Plug, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '@/lib/api/client'
import { useAuthStore } from '@/store/auth'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import {
  VICIDIAL_AGENT_FRAME_HIDDEN_STYLE,
  VICIDIAL_AGENT_FRAME_ID,
  VICIDIAL_AGENT_FRAME_VISIBLE_STYLE,
  VICIDIAL_AGENT_LOGOUT_URL,
  connectVicidialInPage,
  normalizePhilippinesDialValue,
  requestMicrophoneAccess,
  teardownVicidialSession,
  vicidialFailureMessage,
  waitForVicidialAgentLive,
} from '@/lib/vicidial'
import { VICIDIAL_CALL_INTENT_EVENT, type VicidialCallIntent } from '@/lib/vicidialCallIntent'

type PhonePhase = 'offline' | 'connecting' | 'connected' | 'needs_attention'
type CallPhase = 'idle' | 'preview' | 'dialing' | 'requested' | 'wrapup'
type TransferTarget = { id: number; name: string; agent_user: string; state: string; available: boolean }
type TransferOptions = { source_state: string; transfer_enabled: boolean; reason: string; targets: TransferTarget[] }

/**
 * Phone shell — embeds Vicidial agent session in a hidden iframe (CRM pattern).
 * Reconciles the visible agent state from the read-only Agent API status poll.
 */
export function PhoneWidget() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const { showSuccess, showError } = useToastNotification()
  const [expanded, setExpanded] = useState(false)
  const [isHangingUp, setIsHangingUp] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isReadyBusy, setIsReadyBusy] = useState(false)
  const [phase, setPhase] = useState<PhonePhase>('offline')
  const [showAgentScreen, setShowAgentScreen] = useState(false)
  const [availability, setAvailability] = useState<'available' | 'paused'>('available')
  const [callPhase, setCallPhase] = useState<CallPhase>('idle')
  const [agentState, setAgentState] = useState<string | null>(null)
  const [wrapupSince, setWrapupSince] = useState<number | null>(null)
  const [wrapupSecondsLeft, setWrapupSecondsLeft] = useState(30)
  const [hangupRequested, setHangupRequested] = useState(false)
  const [callIntent, setCallIntent] = useState<VicidialCallIntent | null>(null)
  const [quickCall, setQuickCall] = useState(false)
  const [quickCallId, setQuickCallId] = useState<number | null>(null)
  const [recentQuickCallId, setRecentQuickCallId] = useState<number | null>(null)
  const [quickContactName, setQuickContactName] = useState('')
  const [quickPurpose, setQuickPurpose] = useState('')
  const [callOutcome, setCallOutcome] = useState('')
  const [ticketSearch, setTicketSearch] = useState('')
  const [ticketOptions, setTicketOptions] = useState<Array<{id: number; request_number: string; ticket_number?: string | null; subject: string | null}>>([])
  const [ticketSearched, setTicketSearched] = useState(false)
  const [isSearchingTickets, setIsSearchingTickets] = useState(false)
  const [isSavingCall, setIsSavingCall] = useState(false)
  const [recentCalls, setRecentCalls] = useState<Array<{id: number; phone_number: string; contact_name: string | null; ticket_request_id: number | null; dial_status: string; outcome: string | null}>>([])
  const [dialNumber, setDialNumber] = useState('')
  const [callError, setCallError] = useState('')
  const [audioHelp, setAudioHelp] = useState(false)
  const [transferOpen, setTransferOpen] = useState(false)
  const [transferLoading, setTransferLoading] = useState(false)
  const [transferOptions, setTransferOptions] = useState<TransferOptions | null>(null)
  const [transferError, setTransferError] = useState('')
  const [transferSearch, setTransferSearch] = useState('')
  const [transferTargetId, setTransferTargetId] = useState<number | null>(null)
  const [transferMode, setTransferMode] = useState<'consult' | 'direct'>('consult')
  const [placement, setPlacement] = useState<'left' | 'right'>('right')
  const [logoutUrl, setLogoutUrl] = useState<string>(VICIDIAL_AGENT_LOGOUT_URL)
  const autoConnectAttempted = useRef(false)
  const pollAbortRef = useRef<AbortController | null>(null)
  const connectGenerationRef = useRef(0)
  const observedInCallRef = useRef(false)
  const bg = useColorModeValue('white', 'gray.800')
  const border = useColorModeValue('gray.200', 'gray.600')
  const muted = useColorModeValue('gray.500', 'gray.400')
  const audioHelpBg = useColorModeValue('orange.50', 'orange.900')

  const agentUser = user?.vicidial_agent_user?.trim()
  const credentialsConfigured = Boolean(user?.vicidial_credentials_configured)
  const phoneConnected = phase === 'connected'
  const validDialNumber = normalizePhilippinesDialValue(dialNumber)

  useEffect(() => {
    if (agentState !== 'INCALL') {
      setTransferOpen(false)
      setTransferTargetId(null)
    }
  }, [agentState])

  const openTransfer = async (search = '') => {
    if (!phoneConnected || agentState !== 'INCALL') return
    if (search === '') setTransferSearch('')
    setTransferOpen(true)
    setTransferLoading(true)
    setTransferError('')
    setTransferOptions(null)
    setTransferTargetId(null)
    try {
      const response = await apiClient.get<{success: boolean; message?: string; data?: TransferOptions}>(
        '/support/vicidial/transfer-options',
        search.trim().length >= 2 ? { params: { q: search.trim() } } : undefined,
      )
      if (response.data.success && response.data.data) {
        setTransferOptions(response.data.data)
      } else {
        setTransferError(response.data.message || 'Could not load transfer agents.')
      }
    } catch (error) {
      const serverMessage = isAxiosError(error) ? error.response?.data?.message : null
      setTransferError(typeof serverMessage === 'string' && serverMessage
        ? serverMessage
        : 'Could not load transfer agents. Check the phone connection and try again.')
    } finally {
      setTransferLoading(false)
    }
  }

  useEffect(() => {
    const onCallIntent = (event: Event) => {
      if (callPhase === 'dialing' || callPhase === 'requested' || callPhase === 'wrapup' || agentState === 'INCALL' || agentState === 'DISPO' || agentState === 'DEAD') {
        setExpanded(true)
        setCallError('Finish or hang up the current call before starting another.')
        return
      }
      const intent = (event as CustomEvent<VicidialCallIntent>).detail
      if (!intent?.requestId) return
      setCallIntent(intent)
      setQuickCall(false)
      setQuickCallId(null)
      setDialNumber(intent.contactNumber)
      setPlacement(intent.placement ?? 'right')
      setCallError('')
      setAudioHelp(false)
      setCallPhase('preview')
      setExpanded(true)
    }
    window.addEventListener(VICIDIAL_CALL_INTENT_EVENT, onCallIntent)
    return () => window.removeEventListener(VICIDIAL_CALL_INTENT_EVENT, onCallIntent)
  }, [callPhase, agentState])

  const loadRecentCalls = useCallback(async () => {
    try {
      const response = await apiClient.get<{data: typeof recentCalls}>('/support/vicidial/calls')
      setRecentCalls(response.data.data)
    } catch {
      // Call history is supplemental; session controls remain usable.
    }
  }, [])

  useEffect(() => {
    if (expanded) void loadRecentCalls()
  }, [expanded, loadRecentCalls])

  const statusLabel = !agentUser
    ? 'Not mapped'
    : phase === 'connected'
      ? callPhase === 'wrapup' ? wrapupSecondsLeft === 0 ? 'Wrap-up overdue' : 'Wrap-up required'
        : agentState === 'INCALL' ? 'On call'
          : agentState === 'DISPO' ? 'Disposition pending'
            : agentState === 'DEAD' ? 'Phone needs attention'
              : agentState === 'QUEUE' || agentState === 'MQUEUE' ? 'Queued'
                : agentState === 'CLOSER' ? 'Inbound mode'
                  : agentState === 'READY' ? 'Available'
                    : agentState === 'PAUSED' ? 'Paused'
                      : availability === 'available' ? 'Available' : 'Paused'
      : phase === 'connecting'
        ? 'Connecting…'
        : phase === 'needs_attention'
          ? 'Needs attention'
          : 'Offline'
  const statusColor = !agentUser
    ? 'orange'
    : phase === 'connected'
      ? callPhase === 'wrapup' && wrapupSecondsLeft === 0 ? 'red'
        : callPhase === 'wrapup' || agentState === 'DISPO' ? 'orange'
          : agentState === 'DEAD' ? 'red'
            : agentState === 'INCALL' || agentState === 'QUEUE' || agentState === 'MQUEUE' || agentState === 'CLOSER' ? 'blue'
              : agentState === 'READY' ? 'green'
                : agentState === 'PAUSED' ? 'orange'
                  : availability === 'available' ? 'green' : 'orange'
      : phase === 'connecting'
        ? 'blue'
        : phase === 'needs_attention'
          ? 'red'
          : 'gray'
  const statusHeaderBg = {
    orange: 'orange.800',
    green: 'green.700',
    blue: 'blue.700',
    red: 'red.700',
    gray: 'gray.700',
  }[statusColor]

  const requestLoginLink = useCallback(async () => {
    try {
      const response = await apiClient.post<{
        success: boolean
        message?: string
        data?: { path?: string; url?: string; reason?: string; logout_url?: string }
      }>('/support/vicidial/agent-login-link')
      const { success, message, data } = response.data
      if (success && (data?.path || data?.url)) {
        if (data.logout_url) setLogoutUrl(data.logout_url)
        return {
          path: data.path,
          url: data.url,
          logout_url: data.logout_url,
        }
      }
      return { message, reason: data?.reason }
    } catch (error: unknown) {
      const ax = error as {
        response?: { data?: { message?: string; data?: { reason?: string } } }
      }
      if (ax.response?.data) {
        return {
          message: ax.response.data.message,
          reason: ax.response.data.data?.reason,
        }
      }
      throw error
    }
  }, [])

  const fetchAgentStatus = useCallback(async () => {
    const response = await apiClient.get<{
      success: boolean
      data?: { live?: boolean; logout_url?: string; reason?: string; agent_state?: string | null; session_id?: string | null }
    }>('/support/vicidial/agent-status', { timeout: 8000 })
    const live = Boolean(response.data.success && response.data.data?.live)
    const nextLogout = response.data.data?.logout_url
    if (nextLogout) setLogoutUrl(nextLogout)
    return { live, logoutUrl: nextLogout, agentState: response.data.data?.agent_state ?? null }
  }, [])

  useEffect(() => {
    if (!phoneConnected) return
    let disposed = false
    let busy = false
    const check = async () => {
      if (busy || disposed) return
      busy = true
      try {
        const snapshot = await fetchAgentStatus()
        if (disposed) return
        const state = snapshot.agentState
        if (!snapshot.live || !state) return
        setAgentState(state)
        if (state === 'READY') setAvailability('available')
        if (state === 'PAUSED') setAvailability('paused')

        if (state === 'INCALL') observedInCallRef.current = true
        const wasInCall = observedInCallRef.current

        if (state === 'DISPO') {
          if (callPhase !== 'wrapup') {
            setCallPhase('wrapup')
            setWrapupSince(Date.now())
            setExpanded(true)
            if (quickCallId) setRecentQuickCallId(quickCallId)
          }
          return
        }

        if (callPhase === 'wrapup' && (state === 'READY' || state === 'PAUSED')) {
          setCallPhase('idle')
          setCallIntent(null)
          setQuickCall(false)
          setQuickCallId(null)
          setWrapupSince(null)
          setHangupRequested(false)
          observedInCallRef.current = false
          void loadRecentCalls()
          return
        }

        if (callPhase === 'requested' && (wasInCall || hangupRequested) && (state === 'READY' || state === 'PAUSED')) {
          // The PBX already left the call; do not invent or submit a disposition.
          setCallPhase('idle')
          setCallIntent(null)
          setQuickCall(false)
          if (quickCallId) setRecentQuickCallId(quickCallId)
          setQuickCallId(null)
          setHangupRequested(false)
          observedInCallRef.current = false
          setExpanded(true)
          void loadRecentCalls()
        }
      } catch {
        // Preserve the last verified state; a failed status request is not a hangup.
      } finally {
        busy = false
      }
    }
    void check()
    const interval = window.setInterval(() => void check(), 3000)
    return () => { disposed = true; window.clearInterval(interval) }
  }, [phoneConnected, callPhase, fetchAgentStatus, quickCallId, hangupRequested, loadRecentCalls])

  useEffect(() => {
    if (wrapupSince === null) return
    const update = () => setWrapupSecondsLeft(Math.max(0, 30 - Math.floor((Date.now() - wrapupSince) / 1000)))
    update()
    const interval = window.setInterval(update, 1000)
    return () => window.clearInterval(interval)
  }, [wrapupSince])

  const setAgentReady = useCallback(async () => {
    const response = await apiClient.post<{
      success: boolean
      message?: string
      data?: { reason?: string }
    }>('/support/vicidial/ready', undefined, { timeout: 10000 })
    return response.data
  }, [])

  const connectPhone = useCallback(
    async (options?: { quiet?: boolean; requestMic?: boolean }) => {
      pollAbortRef.current?.abort()
      const abort = new AbortController()
      pollAbortRef.current = abort
      const generation = ++connectGenerationRef.current
      const isCurrent = () => connectGenerationRef.current === generation && !abort.signal.aborted

      setIsConnecting(true)
      setPhase('connecting')
      // Keep agent screen visible so the browser can show Vicidial/WebRTC mic prompts.
      setShowAgentScreen(true)

      if (options?.requestMic !== false && !options?.quiet) {
        const mic = await requestMicrophoneAccess()
        if (!isCurrent()) return
        if (!mic.ok && mic.message) {
          showError(mic.message)
        }
      }

      try {
        const result = await connectVicidialInPage(requestLoginLink, {
          signal: abort.signal,
        })
        if (!isCurrent()) return
        if (!result.ok) {
          setPhase('needs_attention')
          if (result.message) showError(result.message)
          return
        }

        if (result.logoutUrl) setLogoutUrl(result.logoutUrl)

        const wait = await waitForVicidialAgentLive(fetchAgentStatus, { signal: abort.signal })
        if (!isCurrent()) return

        if (!wait.live) {
          setPhase('needs_attention')
          setShowAgentScreen(true)
          if (!options?.quiet) {
            showError(
              wait.timedOut
                ? 'Timed out waiting for Vicidial agent session — Allow mic, finish campaign login in the agent panel, then Reconnect.'
                : 'Vicidial login did not become live — use Show agent screen or Reconnect to finish campaign login.',
            )
          }
          return
        }

        if (wait.agentState === 'DISPO' || wait.agentState === 'INCALL') {
          setPhase('connected')
          setAgentState(wait.agentState)
          if (wait.agentState === 'DISPO') {
            setCallPhase('wrapup')
            setWrapupSince(Date.now())
            setExpanded(true)
          }
          setShowAgentScreen(wait.agentState === 'DISPO')
          return
        }

        const ready = await setAgentReady()
        if (!isCurrent()) return

        if (ready.success) {
          setPhase('connected')
          setAvailability('available')
          setCallError('')
          // Keep the webphone running in its full-size hidden iframe after login.
          // Agents can reveal it when browser permission or PBX recovery is needed.
          setShowAgentScreen(false)
          if (!options?.quiet) {
            showSuccess(
              'Phone connected — if audio fails, Allow microphone for the Vicidial host in the agent panel, then Call out.',
            )
          }
        } else {
          setPhase('needs_attention')
          showError(vicidialFailureMessage(ready.data?.reason, ready.message))
        }
      } catch (error) {
        if (!isCurrent()) return
        setPhase('needs_attention')
        showError(error)
      } finally {
        if (connectGenerationRef.current === generation) {
          setIsConnecting(false)
        }
      }
    },
    [fetchAgentStatus, requestLoginLink, setAgentReady, showError, showSuccess],
  )

  // Auto-connect once when credentials are ready.
  useEffect(() => {
    if (!agentUser || !credentialsConfigured) return
    if (autoConnectAttempted.current) return
    autoConnectAttempted.current = true
    void connectPhone({ quiet: true })
  }, [agentUser, credentialsConfigured, connectPhone])

  useEffect(() => {
    const onCleared = () => {
      pollAbortRef.current?.abort()
      connectGenerationRef.current += 1
      setPhase('offline')
      setIsConnecting(false)
      setShowAgentScreen(false)
      setCallPhase('idle')
      setCallIntent(null)
      setCallError('')
    }
    window.addEventListener('atlas-vicidial-session-cleared', onCleared)
    return () => {
      window.removeEventListener('atlas-vicidial-session-cleared', onCleared)
      pollAbortRef.current?.abort()
      connectGenerationRef.current += 1
      autoConnectAttempted.current = false
    }
  }, [])

  useEffect(() => {
    const iframe = document.getElementById(VICIDIAL_AGENT_FRAME_ID) as HTMLIFrameElement | null
    if (!iframe) return
    if (showAgentScreen || phase === 'connecting') {
      iframe.style.cssText = VICIDIAL_AGENT_FRAME_VISIBLE_STYLE
    } else if (phase === 'connected' || phase === 'needs_attention') {
      // Real size off-screen — not 2×2 (Chrome blocks WebRTC mic in tiny iframes).
      iframe.style.cssText = VICIDIAL_AGENT_FRAME_HIDDEN_STYLE
    }
  }, [showAgentScreen, phase])

  // Best-effort teardown when the ATLAS tab is closed/hidden for good.
  useEffect(() => {
    const onPageHide = () => {
      teardownVicidialSession(logoutUrl)
    }
    window.addEventListener('pagehide', onPageHide)
    return () => {
      window.removeEventListener('pagehide', onPageHide)
      pollAbortRef.current?.abort()
    }
  }, [logoutUrl])

  const handleHangup = useCallback(async () => {
    setIsHangingUp(true)
    try {
      const response = await apiClient.post<{
        success: boolean
        message?: string
        data?: { reason?: string }
      }>('/support/vicidial/hangup')
      const { success, message, data } = response.data
      if (success) {
        if (quickCallId) {
          setRecentQuickCallId(quickCallId)
          void loadRecentCalls()
        }
        setHangupRequested(true)
        setCallError('')
        showSuccess(message ?? 'Hangup requested. Waiting for VICIDIAL to confirm call end.')
      } else {
        showError(vicidialFailureMessage(data?.reason, message))
      }
    } catch (error) {
      showError(error)
    } finally {
      setIsHangingUp(false)
    }
  }, [loadRecentCalls, quickCallId, showError, showSuccess])

  const handleReady = useCallback(async () => {
    setIsReadyBusy(true)
    try {
      const response = await apiClient.post<{
        success: boolean
        message?: string
        data?: { reason?: string }
      }>('/support/vicidial/ready')
      if (response.data.success) {
        setPhase('connected')
        setAvailability('available')
        showSuccess(response.data.message ?? 'Agent set to Ready.')
      } else {
        showError(vicidialFailureMessage(response.data.data?.reason, response.data.message))
      }
    } catch (error) {
      showError(error)
    } finally {
      setIsReadyBusy(false)
    }
  }, [showError, showSuccess])

  const handlePause = useCallback(async () => {
    setIsReadyBusy(true)
    try {
      const response = await apiClient.post<{
        success: boolean
        message?: string
        data?: { reason?: string }
      }>('/support/vicidial/pause')
      if (response.data.success) {
        setAvailability('paused')
        showSuccess(response.data.message ?? 'Agent paused.')
      } else {
        showError(vicidialFailureMessage(response.data.data?.reason, response.data.message))
      }
    } catch (error) {
      showError(error)
    } finally {
      setIsReadyBusy(false)
    }
  }, [showError, showSuccess])

  const handleDial = async () => {
    if ((!callIntent && !quickCall) || !validDialNumber || !phoneConnected || callPhase !== 'preview' || agentState === 'INCALL' || agentState === 'DISPO' || agentState === 'DEAD') return
    setCallError('')
    setCallPhase('dialing')
    try {
      const endpoint = quickCall
        ? '/support/vicidial/calls'
        : `/support/my-request/${encodeURIComponent(String(callIntent?.requestId))}/call/vicidial`
      const payload = quickCall
        ? {phone_number: dialNumber.trim(), contact_name: quickContactName.trim() || null, purpose: quickPurpose}
        : {contact_number: dialNumber.trim()}
      const response = await apiClient.post<{
        success: boolean
        message?: string
        data?: { reason?: string; call?: {id: number} }
      }>(endpoint, payload)
      if (response.data.success) {
        if (quickCall) setQuickCallId(response.data.data?.call?.id ?? null)
        observedInCallRef.current = false
        setHangupRequested(false)
        setCallPhase('requested')
        showSuccess('Dial request sent to Vicidial. Check the Phone panel and confirm audio with the recipient.')
      } else {
        setCallPhase('preview')
        setCallError(vicidialFailureMessage(response.data.data?.reason, response.data.message))
        if (response.data.data?.reason === 'AGENT_UNREACHABLE') {
          setPhase('needs_attention')
          setShowAgentScreen(true)
        }
      }
    } catch (error) {
      setCallPhase('preview')
      setCallError('Vicidial dialing is unavailable. Check the agent session and try again.')
      showError(error)
    }
  }

  const searchTickets = async () => {
    if (ticketSearch.trim().length < 2) return
    setIsSearchingTickets(true)
    setTicketSearched(false)
    try {
      const response = await apiClient.get<{data: typeof ticketOptions}>('/support/vicidial/ticket-options', {params: {q: ticketSearch.trim()}})
      setTicketOptions(response.data.data)
      setTicketSearched(true)
    } catch (error) {
      showError(error)
    } finally {
      setIsSearchingTickets(false)
    }
  }

  const attachCall = async (callId: number, ticketId: number) => {
    setIsSavingCall(true)
    try {
      await apiClient.post(`/support/vicidial/calls/${callId}/attach`, {ticket_request_id: ticketId})
      setRecentQuickCallId(null)
      setTicketOptions([])
      setTicketSearched(false)
      setTicketSearch('')
      await loadRecentCalls()
      showSuccess('Call attached to ticket request.')
    } catch (error) {
      showError(error)
    } finally {
      setIsSavingCall(false)
    }
  }

  const saveCallOutcome = async (callId: number) => {
    if (!callOutcome) return
    setIsSavingCall(true)
    try {
      await apiClient.patch(`/support/vicidial/calls/${callId}/outcome`, {outcome: callOutcome})
      setRecentQuickCallId(null)
      setCallOutcome('')
      setTicketOptions([])
      setTicketSearched(false)
      setTicketSearch('')
      await loadRecentCalls()
      showSuccess('Call outcome saved.')
    } catch (error) {
      showError(error)
    } finally {
      setIsSavingCall(false)
    }
  }

  const insecurePhonePage = typeof window !== 'undefined' && window.isSecureContext === false
  const helperText = !agentUser
    ? null
    : !credentialsConfigured
      ? 'Ask an admin to add your Vicidial credentials in User Management.'
      : insecurePhonePage
        ? 'Browser phone audio requires HTTPS or localhost. This network address uses HTTP; open VICIDIAL directly over HTTPS for the audio test.'
      : phase === 'connected'
        ? 'Agent session is live. Audio still needs confirmation on each call.'
        : phase === 'connecting'
          ? 'Signing into Vicidial. Allow microphone if your browser asks.'
          : phase === 'needs_attention'
            ? 'Connection needs attention. Open the agent screen or reconnect.'
            : 'Your phone connects automatically when you sign in.'

  return (
    <Box
      position="fixed"
      right={placement === 'right' && !showAgentScreen ? { base: 3, lg: 4 } : { base: 3, lg: 'auto' }}
      left={placement === 'left' || showAgentScreen ? { base: 3, lg: 20 } : { base: 'auto', lg: 'auto' }}
      top={expanded && callPhase !== 'idle' ? { base: 3, lg: placement === 'left' ? '112px' : 'auto' } : 'auto'}
      bottom={{ base: 3, lg: 4 }}
      zIndex={1500}
      w={{ base: expanded && callPhase !== 'idle' ? 'calc(100% - 24px)' : 'min(100% - 24px, 340px)', lg: '320px' }}
      maxH={{ base: 'calc(100vh - 24px)', lg: 'calc(100vh - 128px)' }}
      bg={bg}
      borderWidth="1px"
      borderColor={border}
      borderRadius="lg"
      boxShadow="xl"
      overflow="hidden"
      aria-label="Phone panel"
    >
      <HStack
        px={4}
        py={3}
        justify="space-between"
        bg={statusHeaderBg}
        color="white"
        borderBottomWidth={expanded ? '1px' : 0}
        borderColor="whiteAlpha.400"
      >
        <HStack spacing={2}>
          <Phone size={18} />
          <Text fontSize="sm" fontWeight="semibold">
            Phone
          </Text>
          <Badge bg="whiteAlpha.300" color="white" textTransform="none">
            {statusLabel}
          </Badge>
          {callPhase === 'requested' ? <Badge bg="whiteAlpha.300" color="white" textTransform="none">{hangupRequested ? 'Ending call' : 'Call requested'}</Badge> : null}
        </HStack>
        <IconButton
          aria-label={expanded ? 'Collapse phone panel' : 'Expand phone panel'}
          icon={expanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          size="sm"
          variant="ghost"
          color="white"
          _hover={{ bg: 'whiteAlpha.300' }}
          _active={{ bg: 'whiteAlpha.400' }}
          onClick={() => setExpanded((v) => !v)}
          isDisabled={callPhase === 'wrapup'}
        />
      </HStack>

      <Collapse in={expanded} animateOpacity>
        <VStack align="stretch" spacing={4} p={4} maxH="calc(100vh - 190px)" overflowY="auto">
          <HStack justify="space-between" align="start">
            <Box>
              <Text fontSize="xs" color={muted}>Vicidial agent</Text>
              <Text fontSize="sm" fontWeight="medium">{agentUser || 'Not mapped'}</Text>
            </Box>
            <Badge colorScheme={phoneConnected ? statusColor : 'gray'} textTransform="none">
              {phoneConnected ? statusLabel : 'Unavailable'}
            </Badge>
          </HStack>
          {helperText ? <Text fontSize="xs" color={muted}>{helperText}</Text> : null}

          {callPhase === 'wrapup' ? (
            <Box borderWidth="1px" borderColor={wrapupSecondsLeft === 0 ? 'red.300' : 'orange.300'} borderRadius="md" p={3} role="alert">
              <Text fontSize="sm" fontWeight="bold">Call ended · disposition required</Text>
              <Text fontSize="xs" color={muted} mt={1}>
                {callIntent?.contactName || quickContactName || 'Vicidial call'}{callIntent?.requestLabel ? ` · ${callIntent.requestLabel}` : ''}
              </Text>
              <Text fontSize="xs" mt={3}>
                {wrapupSecondsLeft > 0
                  ? `Wrap-up reminder in ${wrapupSecondsLeft}s. Complete the disposition to receive another call.`
                  : 'Wrap-up overdue. Complete the disposition before returning to Ready.'}
              </Text>
              <Text fontSize="xs" color={muted} mt={2}>
                VICIDIAL campaign disposition codes are not configured in ATLAS yet. Complete the disposition in the agent screen; this panel will detect when the PBX returns to Ready or Paused.
              </Text>
              <Button size="sm" w="full" mt={3} colorScheme="orange" onClick={() => setShowAgentScreen(true)}>
                Open agent screen to tag disposition
              </Button>
            </Box>
          ) : null}

          {callPhase === 'idle' ? (
            <Button
              size="sm"
              w="full"
              colorScheme="green"
              leftIcon={<PhoneCall size={16} />}
              isDisabled={!phoneConnected || agentState === 'INCALL' || agentState === 'DISPO' || agentState === 'DEAD'}
              onClick={() => {
                setCallIntent(null)
                setQuickCall(true)
                setQuickCallId(null)
                setDialNumber('')
                setQuickContactName('')
                setQuickPurpose('')
                setCallOutcome('')
                setCallError('')
                setCallPhase('preview')
              }}
            >
              Quick call · no ticket needed
            </Button>
          ) : null}

          {(callIntent || quickCall) && callPhase !== 'idle' && callPhase !== 'wrapup' ? (
            <Box borderWidth="1px" borderColor={border} borderRadius="md" p={3}>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="xs" color={muted} fontWeight="semibold" textTransform="uppercase">
                  {agentState === 'INCALL' ? 'Call in progress' : callPhase === 'requested' ? 'Dial request sent' : callPhase === 'dialing' ? 'Sending dial request' : quickCall ? 'Quick call · unlinked' : 'Outbound call'}
                </Text>
                {callPhase === 'preview' ? (
                  <IconButton
                    aria-label="Cancel call preview"
                    icon={<X size={16} />}
                    size="xs"
                    variant="ghost"
                    onClick={() => { setCallPhase('idle'); setCallIntent(null); setQuickCall(false); setCallError('') }}
                  />
                ) : null}
              </HStack>
              {quickCall ? (
                <>
                  <Text fontWeight="semibold">Call a number without a ticket</Text>
                  {callPhase === 'preview' ? (
                    <>
                      <FormControl mt={3}>
                        <FormLabel fontSize="xs">Contact name (optional)</FormLabel>
                        <Input size="sm" value={quickContactName} maxLength={150} onChange={(event) => setQuickContactName(event.target.value)} />
                      </FormControl>
                      <FormControl mt={3} isRequired>
                        <FormLabel fontSize="xs">Purpose</FormLabel>
                        <Select size="sm" placeholder="Choose a purpose" value={quickPurpose} onChange={(event) => setQuickPurpose(event.target.value)}>
                          <option value="follow_up">Follow-up</option>
                          <option value="customer_outreach">Customer outreach</option>
                          <option value="other">Other</option>
                        </Select>
                      </FormControl>
                    </>
                  ) : <Text fontSize="xs" color={muted} mb={3}>Unlinked call · {quickContactName || 'Unknown contact'}</Text>}
                </>
              ) : (
                <>
                  <Text fontWeight="semibold">{callIntent?.contactName}</Text>
                  <Text fontSize="xs" color={muted} mb={3}>{callIntent?.requestLabel}</Text>
                </>
              )}
              {callPhase === 'preview' ? (
                <FormControl isInvalid={Boolean(dialNumber) && !validDialNumber}>
                  <FormLabel fontSize="xs">Phone number</FormLabel>
                  <Input
                    value={dialNumber}
                    onChange={(event) => { setDialNumber(event.target.value); setCallError('') }}
                    inputMode="tel"
                    autoComplete="off"
                    aria-label="Phone number to dial"
                    size="sm"
                  />
                  <Text fontSize="xs" color={validDialNumber ? muted : 'red.500'} mt={1}>
                    {validDialNumber ? `Dial as +63 ${validDialNumber}` : 'Enter a valid Philippine mobile or landline number.'}
                  </Text>
                </FormControl>
              ) : (
                <Text fontSize="sm">{dialNumber}</Text>
              )}
              {callError ? <Text role="alert" fontSize="xs" color="red.600" mt={2}>{callError}</Text> : null}
              {callPhase === 'preview' && phase === 'needs_attention' ? (
                <Button
                  mt={3}
                  w="full"
                  size="sm"
                  leftIcon={<Plug size={14} />}
                  isLoading={isConnecting}
                  onClick={() => void connectPhone({requestMic: true})}
                >
                  Reconnect phone
                </Button>
              ) : null}
              {callPhase === 'preview' ? (
                <Button
                  mt={3}
                  w="full"
                  size="sm"
                  colorScheme="green"
                  leftIcon={<PhoneCall size={16} />}
                  isDisabled={!phoneConnected || !validDialNumber || agentState === 'INCALL' || agentState === 'DISPO' || agentState === 'DEAD' || (quickCall && !quickPurpose)}
                  onClick={() => void handleDial()}
                >
                  Start call
                </Button>
              ) : (
                <VStack align="stretch" mt={3} spacing={2}>
                  <Text fontSize="xs" color={muted}>
                    {hangupRequested
                      ? 'Hangup requested. Waiting for VICIDIAL to confirm the call ended.'
                      : agentState === 'INCALL'
                      ? 'VICIDIAL reports a live call. Confirm two-way audio with the recipient.'
                      : callPhase === 'dialing'
                      ? 'Waiting for Vicidial to accept the dial request.'
                      : 'Vicidial accepted the dial request. Ringing, answer, and audio must be confirmed separately.'}
                  </Text>
                  <Button
                    size="sm"
                    colorScheme="red"
                    variant="outline"
                    leftIcon={<PhoneOff size={16} />}
                    isLoading={isHangingUp}
                    isDisabled={callPhase === 'dialing' || hangupRequested}
                    onClick={() => void handleHangup()}
                  >
                    Hang up
                  </Button>
                  {agentState === 'INCALL' && !hangupRequested ? (
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="blue"
                      leftIcon={<ArrowRightLeft size={16} />}
                      onClick={() => void openTransfer()}
                    >
                      Preview transfer
                    </Button>
                  ) : null}
                </VStack>
              )}
            </Box>
          ) : null}

          {agentState === 'INCALL' && !callIntent && !quickCall && !transferOpen ? (
            <Button size="sm" variant="outline" colorScheme="blue" leftIcon={<ArrowRightLeft size={16} />} onClick={() => void openTransfer()}>
              Preview transfer
            </Button>
          ) : null}

          {transferOpen && agentState === 'INCALL' ? (
            <Box borderWidth="1px" borderColor={border} borderRadius="md" p={3} aria-label="Transfer call panel">
              <HStack justify="space-between" mb={2}>
                <Text fontWeight="semibold" fontSize="sm">Transfer preview</Text>
                <IconButton aria-label="Close transfer panel" icon={<X size={16} />} size="xs" variant="ghost" onClick={() => setTransferOpen(false)} />
              </HStack>
              <Text fontSize="xs" color={muted}>Choose a receiving agent. The customer remains on your call while you review the options.</Text>
              {transferLoading ? <Text fontSize="xs" mt={3}>Checking agent availability…</Text> : null}
              {transferError ? <Text role="alert" color="red.600" fontSize="xs" mt={3}>{transferError}</Text> : null}
              {transferOptions ? (
                <>
                  <FormControl mt={3}>
                    <FormLabel fontSize="xs">Find agent</FormLabel>
                    <HStack>
                      <Input size="sm" placeholder="Email or agent ID" value={transferSearch} onChange={(event) => setTransferSearch(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') void openTransfer(transferSearch) }} />
                      <Button size="sm" isDisabled={transferSearch.trim().length === 1 || transferLoading} onClick={() => void openTransfer(transferSearch)}>Find</Button>
                    </HStack>
                  </FormControl>
                  <VStack align="stretch" spacing={1} mt={2} maxH="180px" overflowY="auto">
                    {transferOptions.targets.map((target) => (
                      <Button
                        key={target.id}
                        size="sm"
                        h="auto"
                        py={2}
                        justifyContent="space-between"
                        variant={transferTargetId === target.id ? 'solid' : 'outline'}
                        colorScheme={transferTargetId === target.id ? 'blue' : undefined}
                        isDisabled={!target.available}
                        onClick={() => setTransferTargetId(target.id)}
                      >
                        <Text fontSize="xs" noOfLines={1}>{target.name} · {target.agent_user}</Text>
                        <Text fontSize="xs" ml={2}>{target.state === 'CLOSER' ? 'Inbound mode' : target.available ? 'Available' : target.state === 'UNKNOWN' ? 'Offline' : target.state}</Text>
                      </Button>
                    ))}
                  </VStack>
                  {transferOptions.targets.length === 0 ? <Text fontSize="xs" color={muted} mt={2}>No matching mapped agents.</Text> : null}
                  {transferOptions.targets.length === 8 ? <Text fontSize="xs" color={muted} mt={2}>Showing the first 8 agents. Search to narrow the list.</Text> : null}
                  <Text fontSize="xs" fontWeight="semibold" mt={3}>Transfer method</Text>
                  <HStack mt={1}>
                    <Button size="xs" variant={transferMode === 'consult' ? 'solid' : 'outline'} colorScheme="blue" onClick={() => setTransferMode('consult')}>Consult first</Button>
                    <Button size="xs" variant={transferMode === 'direct' ? 'solid' : 'outline'} colorScheme="blue" onClick={() => setTransferMode('direct')}>Direct transfer</Button>
                  </HStack>
                  <Text fontSize="xs" color={muted} mt={2}>{transferMode === 'consult' ? 'Speak with the receiving agent before completing the handoff.' : 'The customer is sent to the receiving agent without consultation.'}</Text>
                  <Box bg={audioHelpBg} borderRadius="md" p={2} mt={3}>
                    <Text fontSize="xs" fontWeight="semibold">Transfer is pending PBX verification</Text>
                    <Text fontSize="xs" mt={1}>{transferOptions.reason}</Text>
                  </Box>
                  <Button size="sm" w="full" mt={3} colorScheme="blue" isDisabled>
                    {transferMode === 'consult' ? 'Consult agent' : 'Transfer directly'}
                  </Button>
                </>
              ) : null}
            </Box>
          ) : null}

          {callPhase === 'idle' && recentQuickCallId ? (
            <Box borderWidth="1px" borderColor={border} borderRadius="md" p={3}>
              <Text fontSize="sm" fontWeight="semibold">Quick call · Unlinked</Text>
              <Text fontSize="xs" color={muted} mt={1}>Choose an outcome. If this call belongs to a ticket request, attach it before closing this card.</Text>
              <HStack mt={3}>
                <Select size="sm" aria-label="Call outcome" placeholder="Call outcome" value={callOutcome} onChange={(event) => setCallOutcome(event.target.value)}>
                  <option value="reached">Reached contact</option>
                  <option value="no_answer">No answer</option>
                  <option value="busy">Busy</option>
                  <option value="voicemail">Voicemail</option>
                  <option value="no_audio">No audio</option>
                  <option value="unknown">Unknown</option>
                </Select>
                <Button size="sm" isDisabled={!callOutcome} isLoading={isSavingCall} onClick={() => void saveCallOutcome(recentQuickCallId)}>Save</Button>
              </HStack>
              <FormControl mt={3}>
                <FormLabel fontSize="xs">Attach to ticket request</FormLabel>
                <HStack>
                  <Input size="sm" placeholder="Request number or subject" value={ticketSearch} onChange={(event) => { setTicketSearch(event.target.value); setTicketOptions([]); setTicketSearched(false) }} onKeyDown={(event) => { if (event.key === 'Enter') void searchTickets() }} />
                  <Button size="sm" isDisabled={ticketSearch.trim().length < 2} isLoading={isSearchingTickets} onClick={() => void searchTickets()}>Find</Button>
                </HStack>
              </FormControl>
              <VStack align="stretch" spacing={1} mt={2}>
                {ticketOptions.map((ticket) => (
                  <Button key={ticket.id} size="sm" variant="ghost" h="auto" py={2} justifyContent="start" whiteSpace="normal" textAlign="left" isLoading={isSavingCall} onClick={() => void attachCall(recentQuickCallId, ticket.id)}>
                    {ticket.ticket_number || ticket.request_number} · {ticket.subject || 'No subject'}
                  </Button>
                ))}
              </VStack>
              {ticketSearched && ticketOptions.length === 0 ? <Text fontSize="xs" color={muted}>No matching ticket requests.</Text> : null}
              <HStack mt={3} justify="space-between">
                <Button size="xs" variant="link" onClick={() => navigate('/tickets/create')}>Create ticket</Button>
                <Button size="xs" variant="ghost" onClick={() => { setRecentQuickCallId(null); setTicketOptions([]); setTicketSearched(false) }}>Dismiss for now</Button>
              </HStack>
            </Box>
          ) : null}

          {callPhase === 'idle' && recentCalls.some((call) => call.dial_status === 'accepted' && !call.ticket_request_id && !call.outcome) ? (
            <Box>
              <Text fontSize="xs" color={muted} fontWeight="semibold" mb={1}>Quick calls needing an outcome</Text>
              {recentCalls.filter((call) => call.dial_status === 'accepted' && !call.ticket_request_id && !call.outcome).slice(0, 3).map((call) => (
                <Button key={call.id} size="xs" variant="ghost" display="block" textAlign="left" w="full" onClick={() => { setRecentQuickCallId(call.id); setCallOutcome(call.outcome ?? ''); setTicketOptions([]); setTicketSearched(false) }}>
                  {call.contact_name || `+63 ${call.phone_number}`} · {call.outcome ? call.outcome.replace(/_/g, ' ') : 'Outcome pending'}
                </Button>
              ))}
            </Box>
          ) : null}

          <Divider />
          <HStack spacing={2} flexWrap="wrap">
            <Tooltip label="Set the agent available for calls">
              <Button
                size="sm"
                variant={availability === 'available' ? 'solid' : 'outline'}
                colorScheme={availability === 'available' ? 'green' : undefined}
                isDisabled={!agentUser || !phoneConnected || availability === 'available' || callPhase === 'wrapup' || agentState === 'INCALL'}
                isLoading={isReadyBusy}
                onClick={() => void handleReady()}
              >
                Ready
              </Button>
            </Tooltip>
            <Tooltip label="Pause Vicidial agent via Agent API">
              <Button
                size="sm"
                variant="outline"
                isDisabled={!agentUser || !phoneConnected || availability === 'paused' || callPhase === 'wrapup' || agentState === 'INCALL'}
                isLoading={isReadyBusy}
                onClick={() => void handlePause()}
              >
                Pause
              </Button>
            </Tooltip>
            <Tooltip
              label={
                credentialsConfigured
                  ? phase === 'needs_attention'
                    ? 'Reconnect the embedded Vicidial session'
                    : 'Reconnect hidden Vicidial session'
                  : 'Set Vicidial credentials in User Management first'
              }
            >
              <Button
                size="sm"
                leftIcon={<Plug size={14} />}
                variant="outline"
                isLoading={isConnecting}
                isDisabled={!agentUser || !credentialsConfigured}
                onClick={() =>
                  void connectPhone({
                    requestMic: true,
                  })
                }
              >
                {phase === 'offline' && !isConnecting ? 'Connect phone' : 'Reconnect'}
              </Button>
            </Tooltip>
          </HStack>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<Headphones size={16} />}
            onClick={() => setAudioHelp((value) => !value)}
            aria-expanded={audioHelp}
          >
            {audioHelp ? 'Hide audio help' : 'Audio help'}
          </Button>
          {audioHelp ? (
            <Box bg={audioHelpBg} borderRadius="md" p={3}>
              <Text fontSize="xs">If the call rings without sound, check the webphone and microphone, then ask your PBX admin to inspect the agent phone and conference leg. The last live test ended with SIP cause 44.</Text>
              {phase !== 'offline' ? (
                <Button size="sm" variant="link" mt={2} onClick={() => setShowAgentScreen((value) => !value)}>
                  {showAgentScreen ? 'Hide agent screen' : 'Show agent screen'}
                </Button>
              ) : null}
            </Box>
          ) : phase === 'needs_attention' || phase === 'connecting' || showAgentScreen ? (
            <Button size="sm" variant="link" onClick={() => setShowAgentScreen((value) => !value)}>
              {showAgentScreen ? 'Hide agent screen' : 'Show agent screen'}
            </Button>
          ) : null}
        </VStack>
      </Collapse>
    </Box>
  )
}
